// apps/office/src/pages/Dashboard.jsx
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Users,
  TrendingUp,
  FileText,
  DollarSign,
  Calendar,
  RefreshCw,
  ArrowRight,
  CheckCircle,
  Home,
  Phone,
  Mail,
  Building2,
  Clock,
  AlertCircle,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import {
  AreaChart,
  Area,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from 'recharts';
import { format, subDays, formatDistanceToNow } from 'date-fns';
import { useQuery } from '@tanstack/react-query';
import { toast } from 'sonner';
import pb from '../../lib/pocketbaseClient';
import { useAuth } from '../../contexts/AuthContext';

// Constants
const STATUS_COLORS = {
  Lead: 'bg-blue-100 text-blue-800',
  New: 'bg-blue-100 text-blue-800',
  FollowUp: 'bg-yellow-100 text-yellow-800',
  Connected: 'bg-green-100 text-green-800',
  Confirm: 'bg-purple-100 text-purple-800',
  Converted: 'bg-emerald-100 text-emerald-800',
  Lost: 'bg-red-100 text-red-800',
};

const PIE_COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#8b5cf6', '#ef4444', '#06b6d4', '#ec4899'];

const DATE_RANGES = [
  { value: '7days', label: '7D' },
  { value: '30days', label: '30D' },
  { value: '90days', label: '90D' },
];

// Helper functions
const formatCurrency = (amount) => {
  if (!amount) return '?0';
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
};

const formatDate = (dateString) => {
  if (!dateString) return '�';
  try {
    return format(new Date(dateString), 'dd MMM yyyy');
  } catch {
    return 'Invalid date';
  }
};

// Stat Card Component
const StatCard = ({ title, value, subValue, icon: Icon, iconColor, iconBg, trend, trendValue }) => (
  <Card className="shadow-sm hover:shadow-md transition-all duration-200">
    <CardContent className="p-6">
      <div className="flex justify-between items-start">
        <div>
          <p className="text-sm text-slate-500 font-medium mb-1">{title}</p>
          <p className="text-3xl font-bold text-slate-800">{value}</p>
          {(subValue || trend) && (
            <div className="flex items-center gap-1 mt-2">
              {trend && (
                <TrendingUp className={`w-4 h-4 ${trendValue?.startsWith('+') ? 'text-green-500' : 'text-red-500'}`} />
              )}
              {trendValue && <span className={`text-xs font-medium ${trendValue.startsWith('+') ? 'text-green-500' : 'text-red-500'}`}>{trendValue}</span>}
              {subValue && <span className="text-xs text-slate-400">{subValue}</span>}
            </div>
          )}
        </div>
        <div className={`p-3 rounded-xl ${iconBg}`}>
          <Icon className={`w-6 h-6 ${iconColor}`} />
        </div>
      </div>
    </CardContent>
  </Card>
);

// Main Dashboard Component
const Dashboard = () => {
  const { user: currentUser } = useAuth();
  const navigate = useNavigate();
  const [dateRange, setDateRange] = useState('30days');

  // Determine user role and permissions
  const userRole = currentUser?.role || 'sales';
  const canViewAll = ['admin', 'manager', 'director'].includes(userRole);
  const isSales = userRole === 'sales';
  const isDesigner = userRole === 'designer';

  // Build filter for leads based on user role
  const getLeadFilter = () => {
    if (canViewAll) return '';
    if (isSales) {
      return `(assignedSales = "${currentUser?.id}" || assignedSales = null || assignedSales = "")`;
    }
    if (isDesigner) {
      return `status = "Converted" && assignedDesigner = "${currentUser?.id}"`;
    }
    return '';
  };

  // Fetch leads
  const { data: leads = [], isLoading: leadsLoading, refetch, error: leadsError } = useQuery({
    queryKey: ['dashboardLeads', currentUser?.id, userRole],
    queryFn: async () => {
      if (!currentUser) return [];
      const filter = getLeadFilter();
      try {
        return await pb.collection('leads').getFullList({
          sort: '-created',
          filter,
          expand: 'assignedSales,assignedDesigner',
        });
      } catch (error) {
        console.error('Error fetching leads:', error);
        toast.error('Failed to load leads');
        return [];
      }
    },
    enabled: !!currentUser,
  });

  // Fetch quotations
  const { data: quotations = [] } = useQuery({
    queryKey: ['dashboardQuotations'],
    queryFn: async () => {
      if (!canViewAll && !isSales) return [];
      try {
        return await pb.collection('quotations').getFullList({ 
          sort: '-created',
          filter: canViewAll ? '' : `createdBy = "${currentUser?.id}"`
        });
      } catch (error) {
        console.error('Error fetching quotations:', error);
        return [];
      }
    },
    enabled: !!currentUser && (canViewAll || isSales),
  });

  // Fetch payments
  const { data: payments = [] } = useQuery({
    queryKey: ['dashboardPayments'],
    queryFn: async () => {
      if (!canViewAll && !isSales) return [];
      try {
        return await pb.collection('payments').getFullList({ 
          sort: '-paymentDate',
          filter: canViewAll ? '' : `createdBy = "${currentUser?.id}"`
        });
      } catch (error) {
        console.error('Error fetching payments:', error);
        return [];
      }
    },
    enabled: !!currentUser && (canViewAll || isSales),
  });

  // Calculate metrics
  const totalLeads = leads.length;
  const convertedLeads = leads.filter(l => l.status === 'Converted').length;
  const conversionRate = totalLeads > 0 ? ((convertedLeads / totalLeads) * 100).toFixed(1) : 0;

  const totalQuotesAmount = quotations.reduce((sum, q) => {
    const itemsTotal = q.items?.reduce((s, i) => s + ((i?.rate || 0) * (i?.quantity || 0)), 0) || 0;
    const discount = q.discountAmount || 0;
    const tax = q.taxAmount || 0;
    return sum + (itemsTotal - discount + tax);
  }, 0);

  const totalPaymentsAmount = payments.reduce((sum, p) => sum + (p.amount || 0), 0);

  // Generate lead trend data
  const getLeadTrend = () => {
    const days = dateRange === '7days' ? 7 : dateRange === '30days' ? 30 : 90;
    const today = new Date();
    const trend = [];
    for (let i = days - 1; i >= 0; i--) {
      const date = subDays(today, i);
      const dateStr = format(date, 'yyyy-MM-dd');
      const count = leads.filter(l => {
        if (!l.created) return false;
        return format(new Date(l.created), 'yyyy-MM-dd') === dateStr;
      }).length;
      trend.push({ date: format(date, 'dd MMM'), leads: count });
    }
    return trend;
  };

  // Generate payment trend data
  const getPaymentTrend = () => {
    const days = 30;
    const today = new Date();
    const trend = [];
    for (let i = days - 1; i >= 0; i--) {
      const date = subDays(today, i);
      const dateStr = format(date, 'yyyy-MM-dd');
      const amount = payments
        .filter(p => p.paymentDate && format(new Date(p.paymentDate), 'yyyy-MM-dd') === dateStr)
        .reduce((sum, p) => sum + (p.amount || 0), 0);
      trend.push({ date: format(date, 'dd MMM'), amount });
    }
    return trend;
  };

  // Status distribution
  const statusDistribution = () => {
    const statusCounts = {};
    leads.forEach(lead => {
      const status = lead.status || 'Lead';
      statusCounts[status] = (statusCounts[status] || 0) + 1;
    });
    return Object.entries(statusCounts).map(([name, value]) => ({ name, value }));
  };

  // Recent leads
  const recentLeads = [...leads]
    .sort((a, b) => new Date(b.created) - new Date(a.created))
    .slice(0, 5);

  // Upcoming follow-ups
  const upcomingFollowups = leads
    .filter(l => l.followupDate && new Date(l.followupDate) >= new Date())
    .sort((a, b) => new Date(a.followupDate) - new Date(b.followupDate))
    .slice(0, 5);

  const leadTrend = getLeadTrend();
  const paymentTrend = getPaymentTrend();

  // Loading state
  if (leadsLoading) {
    return (
      <div className="flex flex-col items-center justify-center h-96">
        <RefreshCw className="w-10 h-10 animate-spin text-indigo-500 mb-4" />
        <p className="text-slate-500">Loading dashboard...</p>
      </div>
    );
  }

  // Error state
  if (leadsError) {
    return (
      <div className="flex flex-col items-center justify-center h-96">
        <AlertCircle className="w-12 h-12 text-red-500 mb-4" />
        <p className="text-slate-700 font-medium">Failed to load dashboard</p>
        <p className="text-slate-400 text-sm mt-1">{leadsError.message}</p>
        <Button onClick={() => refetch()} className="mt-4">Retry</Button>
      </div>
    );
  }

  return (
    <div className="space-y-6 pb-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">Dashboard</h1>
          <p className="text-slate-500 text-sm mt-1">
            Welcome back, {currentUser?.name || 'User'}! ??
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Button variant="outline" size="sm" onClick={() => refetch()} className="gap-2">
            <RefreshCw className="w-4 h-4" /> Refresh
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          title="Total Leads"
          value={totalLeads}
          subValue={`Conversion: ${conversionRate}%`}
          icon={Users}
          iconColor="text-indigo-600"
          iconBg="bg-indigo-100"
          trend
          trendValue={conversionRate > 0 ? `+${conversionRate}%` : '0%'}
        />
        <StatCard
          title="Converted"
          value={convertedLeads}
          subValue={`${convertedLeads} of ${totalLeads} leads`}
          icon={CheckCircle}
          iconColor="text-emerald-600"
          iconBg="bg-emerald-100"
        />
        <StatCard
          title="Quotations Value"
          value={formatCurrency(totalQuotesAmount)}
          subValue={`${quotations.length} quotations sent`}
          icon={FileText}
          iconColor="text-purple-600"
          iconBg="bg-purple-100"
        />
        <StatCard
          title="Total Payments"
          value={formatCurrency(totalPaymentsAmount)}
          subValue={`${payments.length} transactions`}
          icon={DollarSign}
          iconColor="text-green-600"
          iconBg="bg-green-100"
        />
      </div>

      {/* Charts Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Lead Trend Chart */}
        <Card className="shadow-sm">
          <CardHeader className="pb-2">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
              <CardTitle className="text-base">Lead Acquisition Trend</CardTitle>
              <div className="flex gap-1">
                {DATE_RANGES.map(range => (
                  <Button
                    key={range.value}
                    variant={dateRange === range.value ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setDateRange(range.value)}
                    className="h-8 px-3 text-xs"
                  >
                    {range.label}
                  </Button>
                ))}
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="h-72">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={leadTrend}>
                  <defs>
                    <linearGradient id="leadGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#6366f1" stopOpacity={0.3} />
                      <stop offset="95%" stopColor="#6366f1" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                  <Tooltip 
                    contentStyle={{ fontSize: 12, borderRadius: '8px' }}
                    formatter={(value) => [`${value} leads`, 'Leads']}
                  />
                  <Area 
                    type="monotone" 
                    dataKey="leads" 
                    stroke="#6366f1" 
                    strokeWidth={2} 
                    fill="url(#leadGradient)"
                    activeDot={{ r: 6 }}
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        {/* Payment Trend Chart */}
        <Card className="shadow-sm">
          <CardHeader>
            <CardTitle className="text-base">Payment Collection Trend (Last 30 days)</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-72">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={paymentTrend}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                  <YAxis tickFormatter={(v) => `?${v/1000}k`} tick={{ fontSize: 11 }} />
                  <Tooltip 
                    contentStyle={{ fontSize: 12, borderRadius: '8px' }}
                    formatter={(value) => [formatCurrency(value), 'Amount']}
                  />
                  <Line 
                    type="monotone" 
                    dataKey="amount" 
                    stroke="#10b981" 
                    strokeWidth={2} 
                    dot={{ fill: '#10b981', r: 4 }}
                    activeDot={{ r: 6 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Status Distribution & Upcoming Follow-ups */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className="shadow-sm lg:col-span-1">
          <CardHeader>
            <CardTitle className="text-base">Lead Status Distribution</CardTitle>
          </CardHeader>
          <CardContent>
            {statusDistribution().length === 0 ? (
              <div className="text-center py-8 text-slate-400">
                <PieChart className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                <p className="text-sm">No data available</p>
              </div>
            ) : (
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={statusDistribution()}
                      cx="50%"
                      cy="50%"
                      innerRadius={50}
                      outerRadius={80}
                      paddingAngle={3}
                      dataKey="value"
                      label={({ name, percent }) => 
                        `${name} ${(percent * 100).toFixed(0)}%`
                      }
                      labelLine={false}
                      fontSize={10}
                    >
                      {statusDistribution().map((_, idx) => (
                        <Cell key={`cell-${idx}`} fill={PIE_COLORS[idx % PIE_COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip 
                      contentStyle={{ fontSize: 12, borderRadius: '8px' }}
                      formatter={(value) => [`${value} leads`, 'Count']}
                    />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            )}
          </CardContent>
        </Card>

        <Card className="shadow-sm lg:col-span-2">
          <CardHeader>
            <CardTitle className="text-base">Upcoming Follow-ups (Next 7 days)</CardTitle>
          </CardHeader>
          <CardContent>
            {upcomingFollowups.length === 0 ? (
              <div className="text-center py-8 text-slate-400">
                <Calendar className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                <p className="text-sm">No follow-ups scheduled</p>
              </div>
            ) : (
              <div className="space-y-3 max-h-72 overflow-y-auto pr-2">
                {upcomingFollowups.map(lead => (
                  <div
                    key={lead.id}
                    className="flex flex-col sm:flex-row justify-between items-start sm:items-center p-3 border rounded-lg hover:bg-slate-50 cursor-pointer transition-all duration-200 gap-2"
                    onClick={() => navigate(`/office/clients/${lead.id}`)}
                  >
                    <div className="min-w-0 flex-1">
                      <p className="font-semibold text-slate-800 truncate">{lead.name}</p>
                      <div className="flex flex-wrap items-center gap-2 mt-1">
                        <Badge className={STATUS_COLORS[lead.status] || 'bg-gray-100 text-gray-800'}>
                          {lead.status || 'Lead'}
                        </Badge>
                        <span className="text-xs text-slate-400">
                          Job ID: {lead.jobId || '�'}
                        </span>
                      </div>
                    </div>
                    <div className="flex items-center gap-3 text-sm text-amber-600 shrink-0">
                      <Calendar className="w-4 h-4" />
                      <span className="font-medium">{formatDate(lead.followupDate)}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Recent Leads */}
      <Card className="shadow-sm">
        <CardHeader className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
          <CardTitle className="text-base">Recent Leads</CardTitle>
          <Button variant="ghost" size="sm" onClick={() => navigate('/office/clients')} className="text-indigo-600 gap-1">
            View All <ArrowRight className="w-4 h-4" />
          </Button>
        </CardHeader>
        <CardContent>
          {recentLeads.length === 0 ? (
            <div className="text-center py-8 text-slate-400">
              <Users className="w-12 h-12 mx-auto mb-3 text-slate-300" />
              <p className="text-sm">No leads yet</p>
            </div>
          ) : (
            <div className="space-y-3">
              {recentLeads.map(lead => (
                <div
                  key={lead.id}
                  className="flex flex-col sm:flex-row justify-between items-start sm:items-center p-3 border rounded-lg hover:bg-slate-50 cursor-pointer transition-all duration-200 gap-2"
                  onClick={() => navigate(`/office/clients/${lead.id}`)}
                >
                  <div className="min-w-0 flex-1">
                    <p className="font-semibold text-slate-800 truncate">{lead.name}</p>
                    <div className="flex flex-wrap items-center gap-2 mt-1">
                      <span className="text-xs text-slate-500 truncate">{lead.email || '�'}</span>
                      <span className="text-xs text-slate-300">�</span>
                      <span className="text-xs text-slate-500">{lead.phone || '�'}</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 shrink-0">
                    <Badge className={STATUS_COLORS[lead.status] || 'bg-gray-100 text-gray-800'}>
                      {lead.status || 'Lead'}
                    </Badge>
                    <span className="text-xs text-slate-400">{formatDate(lead.created)}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Quick Actions */}
      <Card className="shadow-sm">
        <CardHeader>
          <CardTitle className="text-base">Quick Actions</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-3">
            {(canViewAll) && (
              <Button onClick={() => navigate('/office/clients/new')} className="gap-2">
                <Users className="w-4 h-4" /> Onboard New Client
              </Button>
            )}
            {(canViewAll || isSales) && (
              <Button variant="outline" onClick={() => navigate('/office/quotations')} className="gap-2">
                <FileText className="w-4 h-4" /> Create Quotation
              </Button>
            )}
            {(canViewAll) && (
              <Button variant="outline" onClick={() => navigate('/office/properties')} className="gap-2">
                <Building2 className="w-4 h-4" /> Manage Properties
              </Button>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default Dashboard;